import React from 'react';
import { array, bool, func, number, object, shape, string } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import loadable from '@loadable/component';

import { types as sdkTypes } from '../../util/sdkLoader';
import { propTypes } from '../../util/types';

import { authenticationInProgress, logout } from '../../ducks/auth.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling } from '../../ducks/ui.duck';
import {
  fetchCurrentUserNotifications,
  hasCurrentUserErrors,
  sendVerificationEmail,
} from '../../ducks/user.duck';
import { transitions } from '../../transactions/transactionProcessAcceptBid';
import { sendReview } from '../TransactionPage/TransactionPage.duck';

const { UUID } = sdkTypes;

const Topbar = loadable(() => import(/* webpackChunkName: "Topbar" */ './Topbar/Topbar'));

export const TopbarContainerComponent = props => {
  const {
    authInProgress,
    currentPage,
    currentSearchParams,
    currentUser,
    businessListing,
    currentUserHasListings,
    currentUserHasOrders,
    history,
    isAuthenticated,
    authScopes,
    hasGenericError,
    location,
    notificationCount,
    onLogout,
    onManageDisableScrolling,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    onResendVerificationEmail,
    ...rest
  } = props;

  return (
    <Topbar
      authInProgress={authInProgress}
      currentPage={currentPage}
      currentSearchParams={currentSearchParams}
      currentUser={currentUser}
      businessListing={businessListing}
      currentUserHasListings={currentUserHasListings}
      currentUserHasOrders={currentUserHasOrders}
      history={history}
      isAuthenticated={isAuthenticated}
      authScopes={authScopes}
      location={location}
      notificationCount={notificationCount}
      onLogout={onLogout}
      onManageDisableScrolling={onManageDisableScrolling}
      onResendVerificationEmail={onResendVerificationEmail}
      sendVerificationEmailInProgress={sendVerificationEmailInProgress}
      sendVerificationEmailError={sendVerificationEmailError}
      showGenericError={hasGenericError}
      {...rest}
    />
  );
};

TopbarContainerComponent.defaultProps = {
  currentPage: null,
  currentSearchParams: null,
  currentUser: null,
  currentUserHasOrders: null,
  notificationCount: 0,
  sendVerificationEmailError: null,
  authScopes: null,
};

TopbarContainerComponent.propTypes = {
  authInProgress: bool.isRequired,
  currentPage: string,
  currentSearchParams: object,
  currentUser: propTypes.currentUser,
  currentUserHasListings: bool.isRequired,
  currentUserHasOrders: bool,
  isAuthenticated: bool.isRequired,
  authScopes: array,
  notificationCount: number,
  onLogout: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  sendVerificationEmailInProgress: bool.isRequired,
  sendVerificationEmailError: propTypes.error,
  onResendVerificationEmail: func.isRequired,
  hasGenericError: bool.isRequired,

  // from withRouter
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({ state: object }).isRequired,
};

const mapStateToProps = state => {
  // Topbar needs isAuthenticated
  const { isAuthenticated, logoutError, authScopes } = state.auth;
  // Topbar needs user info.
  const {
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    currentUserNotificationCount: notificationCount,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    reviewTransactionRefs,
  } = state.user;
  const {
    sendReviewInProgress,
    sendReviewError,
  } = state.TransactionPage;
  const hasGenericError = !!(logoutError || hasCurrentUserErrors(state));
  const { businessListingId } = (currentUser && currentUser.id && currentUser.attributes.profile.protectedData) || {};
  const ref = { id: new UUID(businessListingId), type: 'ownListing' };
  const businessListings = getMarketplaceEntities(state, [ref]);
  const businessListing = businessListings.length ? businessListings[0] : null;
  const reviewTransactions = getMarketplaceEntities(state, reviewTransactionRefs);
  const filteredReviewTransactions = reviewTransactions.length && currentUser && currentUser.id
      ? reviewTransactions.filter(transaction => {
        const { lastTransition } = transaction.attributes || {};

        if (currentUser.id.uuid == transaction.provider.id.uuid) {
          return [transitions.OPERATOR_COMPLETE, transitions.REVIEW_1_BY_CUSTOMER].includes(lastTransition);
        } else if (currentUser.id.uuid == transaction.customer.id.uuid) {
          return [transitions.OPERATOR_COMPLETE, transitions.REVIEW_1_BY_PROVIDER].includes(lastTransition);
        }
        return false;
      })
      : [];

  return {
    authInProgress: authenticationInProgress(state),
    currentUser,
    businessListing,
    currentUserHasListings,
    currentUserHasOrders,
    sendReviewInProgress,
    sendReviewError,
    reviewTransactions: filteredReviewTransactions,
    notificationCount,
    isAuthenticated,
    authScopes,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    hasGenericError,
  };
};

const mapDispatchToProps = dispatch => ({
  onLogout: historyPush => dispatch(logout(historyPush)),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onResendVerificationEmail: () => dispatch(sendVerificationEmail()),
  onFetchCurrentUserNotifications: () => dispatch(fetchCurrentUserNotifications()),
  onSendReview: (tx, transitionOptions, params, config) => dispatch(sendReview(tx, transitionOptions, params, config)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const TopbarContainer = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(TopbarContainerComponent);

export default TopbarContainer;
